import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalPharmacy as PharmacyIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Medication as MedicationIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchMyPrescriptions } from '../../store/slices/prescriptionSlice';

interface Prescription {
  id: number;
  prescriptionNumber: string;
  doctorName: string;
  doctorSpecialization: string;
  patientName: string;
  prescriptionDate: string;
  status: string;
  statusDisplayName: string;
  medicationCount: number;
  diagnosis: string;
  hasPrescription: boolean;
  createdAt: string;
}

const PrescriptionListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { prescriptions, loading, error } = useSelector((state: RootState) => state.prescription);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(fetchMyPrescriptions() as any);
  }, [dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return { bg: '#d1fae5', color: '#065f46' };
      case 'COMPLETED': return { bg: '#dbeafe', color: '#1e40af' };
      case 'EXPIRED': return { bg: '#fee2e2', color: '#991b1b' };
      case 'CANCELLED': return { bg: '#f3f4f6', color: '#374151' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const filteredPrescriptions = (prescriptions || []).filter((p: Prescription) => {
    const matchesSearch = !searchQuery ||
      p.prescriptionNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.doctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.diagnosis?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Prescriptions</Typography>
          <Typography variant="body2" color="#6b7280">View and manage your medical prescriptions</Typography>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by prescription number, doctor, or diagnosis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="EXPIRED">Expired</option>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="body2" color="#6b7280">{filteredPrescriptions.length} records</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress />}

      {/* Prescriptions Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Prescription #</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Diagnosis</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Medicines</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrescriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                    <MedicationIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                    <Typography color="#6b7280">No prescriptions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrescriptions.map((prescription: Prescription) => (
                  <TableRow key={prescription.id} hover sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'transparent' , color: '#2563eb' }}>
                          <PharmacyIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {prescription.prescriptionNumber}
                          </Typography>
                          <Typography variant="caption" color="#6b7280">{prescription.doctorSpecialization}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{prescription.doctorName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">{prescription.prescriptionDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">{prescription.diagnosis || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${prescription.medicationCount || 0} items`} size="small" sx={{ bgcolor: 'transparent' , color: '#374151', fontWeight: 500 }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={prescription.statusDisplayName || prescription.status}
                        size="small"
                        sx={{ bgcolor: getStatusColor(prescription.status).bg, color: getStatusColor(prescription.status).color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => navigate(`/prescriptions/${prescription.id}`)} sx={{ color: '#2563eb' }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#059669' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PrescriptionListPage;